import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { Container, Table, Form, Row, Col, Card, Spinner, Button, Pagination, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChatLogs, fetchFeedbackStats } from '../features/logSlice';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../lib/api';

const ChatLogsPage = () => {
  const dispatch = useDispatch();
  const { list, loading, feedbackCounts, totalPages } = useSelector((state) => state.logs);
  const token = useSelector((state) => state.auth.token);

  const [filters, setFilters] = useState({
    email: '',
    ip_address: '',
    guest_name: '',
    feedback: '',
  });

  const [page, setPage] = useState(1);
  const [collapsed, setCollapsed] = useState(false);

  const [actMsg, setActMsg] = useState('');
  const [actErr, setActErr] = useState('');

  const [identityDel, setIdentityDel] = useState('');
  const [identityKeys, setIdentityKeys] = useState('');

  useEffect(() => {
    dispatch(fetchChatLogs({ token, filters, page }));
    dispatch(fetchFeedbackStats(token));
  }, [dispatch, token, filters, page]);

  const handleSearch = () => {
    setPage(1);
    dispatch(fetchChatLogs({ token, filters, page: 1 }));
  };

  const count = (type) =>
    feedbackCounts.find((item) => item.feedback === type)?.total || 0;

  const exportCSV = () => {
    const rows = list.map((log) => ({
      user: log.email || log.guest_name || 'Anonymous',
      feedback: log.feedback || '-',
      question: log.question,
      response: log.response,
      ip: log.ip_address,
      time: new Date(log.created_at).toLocaleString(),
      thread: log.thread_id || '-',
    }));

    const csv = [
      ['User', 'Feedback', 'Question', 'Response', 'IP', 'Time', 'Thread'],
      ...rows.map((r) => Object.values(r)),
    ]
      .map((row) => row.map((val) => `"${String(val).replaceAll('"', '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'chat_logs.csv');
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [['User', 'Feedback', 'Question', 'Response', 'IP', 'Time', 'Thread']],
      body: list.map((log) => [
        log.email || log.guest_name || 'Anonymous',
        log.feedback || '-',
        log.question,
        log.response,
        log.ip_address,
        new Date(log.created_at).toLocaleString(),
        log.thread_id || '-',
      ]),
      styles: { cellWidth: 'wrap' },
      columnStyles: {
        2: { cellWidth: 60 },
        3: { cellWidth: 60 },
        6: { cellWidth: 50 },
      },
      headStyles: { fillColor: [33, 37, 41] },
      theme: 'striped',
      didDrawPage: () => {
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 10);
      },
    });
    doc.save('chat_logs.pdf');
  };

  const doDeleteThreadChats = async (thread_id) => {
    try {
      if (!thread_id) return;
      await api.delete(`/admin/chats/${encodeURIComponent(thread_id)}/`);
      setActMsg(`Deleted chats for thread: ${thread_id}`);
      dispatch(fetchChatLogs({ token, filters, page }));
      setTimeout(() => setActMsg(''), 2000);
    } catch (err) {
      setActErr(err?.response?.data?.error || err?.message || 'Delete failed');
      setTimeout(() => setActErr(''), 3000);
    }
  };

  const doDeleteThreadSummary = async (thread_id) => {
    try {
      if (!thread_id) return;
      await api.delete(`/admin/summary/${encodeURIComponent(thread_id)}/`);
      setActMsg(`Deleted summary for thread: ${thread_id}`);
      setTimeout(() => setActMsg(''), 2000);
    } catch (err) {
      setActErr(err?.response?.data?.error || err?.message || 'Delete failed');
      setTimeout(() => setActErr(''), 3000);
    }
  };

  const doDeleteIdentityMemory = async () => {
    try {
      if (!identityDel.trim()) {
        setActErr('Identity is required');
        setTimeout(() => setActErr(''), 2000);
        return;
      }
      const params = new URLSearchParams({ identity: identityDel.trim() });
      identityKeys
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((k) => params.append('key', k));

      await api.delete(`/admin/memory/?${params.toString()}`);
      setActMsg(
        `Deleted memory for identity: ${identityDel}${
          identityKeys.trim() ? ` (keys: ${identityKeys})` : ''
        }`
      );
      setIdentityDel('');
      setIdentityKeys('');
      setTimeout(() => setActMsg(''), 2500);
    } catch (err) {
      setActErr(err?.response?.data?.error || err?.message || 'Delete failed');
      setTimeout(() => setActErr(''), 3000);
    }
  };

  return (
    <div className="d-flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <Container
        fluid
        style={{ marginLeft: collapsed ? '72px' : '220px', transition: 'margin-left .2s ease' }}
        className="pt-4"
      >
        <h4>Chat Logs</h4>

        {actMsg ? <Alert variant="success" className="py-2">{actMsg}</Alert> : null}
        {actErr ? <Alert variant="danger" className="py-2">{actErr}</Alert> : null}

        <Card className="p-3 mb-3 shadow-sm">
          <h6 className="mb-2">Admin Tools</h6>
          <Row className="g-2 align-items-end">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Delete Identity Memory</Form.Label>
                <Form.Control
                  placeholder="identity e.g. email:ada@x.com or guest:abcd1234|ip:1.2.3.4"
                  value={identityDel}
                  onChange={(e) => setIdentityDel(e.target.value)}
                />
                <Form.Text className="text-muted">Deletes KV memory only.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Limit to keys (optional, comma-separated)</Form.Label>
                <Form.Control
                  placeholder="e.g. style.tone, location"
                  value={identityKeys}
                  onChange={(e) => setIdentityKeys(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="text-end">
              <Button variant="danger" onClick={doDeleteIdentityMemory} style={{ cursor: 'pointer' }}>
                Delete Memory
              </Button>
            </Col>
          </Row>
        </Card>

        <Card className="p-3 mb-3 shadow-sm">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <i className="bi bi-hand-thumbs-up-fill text-success me-2" /> Positive:{' '}
              <strong>{count('positive')}</strong>
            </div>
            <div>
              <i className="bi bi-hand-thumbs-down-fill text-danger me-2" /> Negative:{' '}
              <strong>{count('negative')}</strong>
            </div>
            <div className="ms-auto">
              <Button size="sm" className="me-2" onClick={exportCSV} style={{ cursor: 'pointer' }}>
                Export CSV
              </Button>
              <Button size="sm" variant="dark" onClick={exportPDF} style={{ cursor: 'pointer' }}>
                Export PDF
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-3 mb-3 shadow-sm">
          <Row>
            <Col md={3}>
              <Form.Control
                placeholder="Email"
                value={filters.email}
                onChange={(e) => setFilters({ ...filters, email: e.target.value })}
              />
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder="IP Address"
                value={filters.ip_address}
                onChange={(e) => setFilters({ ...filters, ip_address: e.target.value })}
              />
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder="Guest Name"
                value={filters.guest_name}
                onChange={(e) => setFilters({ ...filters, guest_name: e.target.value })}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.feedback}
                onChange={(e) => setFilters({ ...filters, feedback: e.target.value })}
                style={{ cursor: 'pointer' }}
              >
                <option value="">All Feedback</option>
                <option value="positive">👍 Positive</option>
                <option value="negative">👎 Negative</option>
              </Form.Select>
            </Col>
          </Row>
          <div className="text-end mt-3">
            <Button onClick={handleSearch} style={{ cursor: 'pointer' }}>
              Search Logs
            </Button>
          </div>
        </Card>

        <Card className="p-3 shadow-sm">
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>Email / Guest</th>
                <th>Feedback</th>
                <th>Question</th>
                <th>Response</th>
                <th>IP</th>
                <th>Agent</th>
                <th>Time</th>
                <th>Thread</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    <Spinner animation="border" />
                  </td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    No logs found
                  </td>
                </tr>
              ) : (
                list.map((log) => (
                  <tr key={log.id}>
                    <td>{log.email || log.guest_name || 'Anonymous'}</td>
                    <td>
                      {log.feedback === 'positive' && (
                        <i className="bi bi-hand-thumbs-up-fill text-success" title="Positive" />
                      )}
                      {log.feedback === 'negative' && (
                        <i className="bi bi-hand-thumbs-down-fill text-danger" title="Negative" />
                      )}
                    </td>
                    <td>{log.question}</td>
                    <td>{log.response}</td>
                    <td>{log.ip_address}</td>
                    <td>{log.user_agent?.slice(0, 20)}...</td>
                    <td>{new Date(log.created_at).toLocaleString()}</td>
                    <td style={{ maxWidth: 240, wordBreak: 'break-word' }}>
                      {log.thread_id || '-'}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => doDeleteThreadChats(log.thread_id)}
                          disabled={!log.thread_id}
                          title="Delete all chats in this thread"
                          style={{ cursor: 'pointer' }}
                        >
                          Del Chats
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-warning"
                          onClick={() => doDeleteThreadSummary(log.thread_id)}
                          disabled={!log.thread_id}
                          title="Delete rolling summary for this thread"
                          style={{ cursor: 'pointer' }}
                        >
                          Del Summary
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={page === i + 1}
                  onClick={() => setPage(i + 1)}
                  style={{ cursor: 'pointer' }}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          )}
        </Card>
      </Container>
    </div>
  );
};

export default ChatLogsPage;
